
import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from 'date-fns';
import { CalendarIcon, X, Filter, Search } from 'lucide-react';
import { cn } from "@/lib/utils";

interface CurrentAffairsFilterProps {
  onFilterChange: (filters: {
    searchTerm: string;
    language: string;
    timePeriod: string;
    date: Date | undefined;
  }) => void;
  onClearFilters: () => void;
  filters: {
    searchTerm: string;
    language: string;
    timePeriod: string;
    date: Date | undefined;
  };
}

// Language options for the dropdown
const languageOptions = [
  { value: "all", label: "All Languages" },
  { value: "english", label: "English" },
  { value: "hindi", label: "Hindi" },
  { value: "marathi", label: "Marathi" },
  { value: "tamil", label: "Tamil" },
  { value: "telugu", label: "Telugu" },
  { value: "kannada", label: "Kannada" },
  { value: "bengali", label: "Bengali" },
  { value: "gujarati", label: "Gujarati" },
  { value: "punjabi", label: "Punjabi" },
];

// Time period options for the dropdown
const timePeriodOptions = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_week", label: "Last Week" },
  { value: "last_month", label: "Last Month" },
  { value: "last_quarter", label: "Last Quarter" },
  { value: "last_year", label: "Last Year" },
];

export function CurrentAffairsFilter({ onFilterChange, onClearFilters, filters }: CurrentAffairsFilterProps) {
  // Handler for updating the filter state
  const handleFilterChange = (key: string, value: string | Date | undefined) => {
    onFilterChange({
      ...filters,
      [key]: value,
    });
  };
  
  const hasActiveFilters = 
    filters.searchTerm !== '' || 
    filters.language !== 'all' || 
    filters.timePeriod !== 'all' || 
    filters.date !== undefined;
  
  return (
    <Card className="mb-4">
      <CardContent className="p-3 sm:p-4">
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search input */}
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search articles..."
                className="pl-9"
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              />
            </div>
            
            {/* Clear filters button shown only when filters are active */}
            {hasActiveFilters && (
              <Button 
                variant="outline" 
                size="sm" 
                className="h-10" 
                onClick={onClearFilters}
              >
                <X className="mr-2 h-4 w-4" />
                Clear Filters
              </Button>
            )}
          </div>
          
          <div className="flex flex-wrap gap-3">
            {/* Language filter */}
            <div className="w-full sm:w-auto">
              <Select
                value={filters.language}
                onValueChange={(value) => handleFilterChange('language', value)}
              >
                <SelectTrigger className="h-9 w-full sm:w-[180px]">
                  <SelectValue placeholder="Language" />
                </SelectTrigger>
                <SelectContent>
                  {languageOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Time period filter */}
            <div className="w-full sm:w-auto">
              <Select
                value={filters.timePeriod}
                onValueChange={(value) => handleFilterChange('timePeriod', value)}
              >
                <SelectTrigger className="h-9 w-full sm:w-[180px]">
                  <SelectValue placeholder="Time Period" />
                </SelectTrigger>
                <SelectContent>
                  {timePeriodOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Date picker */}
            <div className="w-full sm:w-auto">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "h-9 w-full sm:w-[180px] justify-start text-left font-normal",
                      !filters.date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.date ? format(filters.date, 'PP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.date}
                    onSelect={(date) => handleFilterChange('date', date)}
                    initialFocus
                  />
                  {/* Add option to clear date selection */}
                  {filters.date && (
                    <div className="p-2 border-t border-border">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="w-full text-xs" 
                        onClick={() => handleFilterChange('date', undefined)}
                      >
                        <X className="mr-2 h-3 w-3" /> Clear date
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
